import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { AdminSection } from "./admin";
import { AdminButton, AdminDialog, Field, Input, TableShell } from "@/components/admin/AdminUI";
import {
  CONTENT_KINDS, CONTENT_KIND_IDS, type ContentKindId, type QualityIssue,
} from "@/lib/content-ops";
import {
  cancelContentSchedule, contentOverview, contentVersions, listContent, publishContent,
  relatedContent, restoreContentVersion, scheduleContent, snapshotContent,
} from "@/lib/content-ops.functions";

export const Route = createFileRoute("/_authenticated/admin/content")({
  component: ContentOpsAdmin,
});

type Item = Awaited<ReturnType<typeof listContent>>[number];
type Overview = Awaited<ReturnType<typeof contentOverview>>;

const STATE_STYLE: Record<string, string> = {
  published: "text-emerald bg-emerald/10 border-emerald/30",
  scheduled: "text-gold bg-gold/10 border-gold/30",
  draft: "text-muted-foreground border hairline",
  always: "text-violet-300 bg-violet-500/10 border-violet-400/30",
};

function IssueDots({ issues }: { issues: QualityIssue[] }) {
  const errors = issues.filter((i) => i.level === "error").length;
  const warns = issues.filter((i) => i.level === "warn").length;
  if (!errors && !warns) return <span className="text-xs text-emerald">clean</span>;
  return (
    <span className="text-xs text-muted-foreground">
      {errors > 0 && <span className="text-red-400">{errors} blocking</span>}
      {errors > 0 && warns > 0 && " · "}
      {warns > 0 && <span className="text-amber-300">{warns} warn</span>}
    </span>
  );
}

function ContentOpsAdmin() {
  const list = useServerFn(listContent);
  const overviewFn = useServerFn(contentOverview);
  const publish = useServerFn(publishContent);
  const schedule = useServerFn(scheduleContent);
  const cancel = useServerFn(cancelContentSchedule);
  const snapshot = useServerFn(snapshotContent);

  const [kind, setKind] = useState<ContentKindId>("activation");
  const [items, setItems] = useState<Item[]>([]);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<"all" | "draft" | "scheduled" | "published">("all");
  const [q, setQ] = useState("");
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [inspect, setInspect] = useState<Item | null>(null);

  async function loadList(k: ContentKindId) {
    setLoading(true);
    setError(null);
    try {
      setItems(await list({ data: { kind: k, limit: 300 } }));
      setSelected(new Set());
    } catch (e) {
      setError((e as Error).message);
      setItems([]);
    }
    setLoading(false);
  }

  async function loadOverview() {
    try {
      setOverview(await overviewFn({}));
    } catch (e) {
      setError((e as Error).message);
    }
  }

  useEffect(() => { void loadList(kind); }, [kind]);
  useEffect(() => { void loadOverview(); }, []);

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((i) => {
      if (filter !== "all" && i.state !== filter) return false;
      if (!term) return true;
      return `${i.title} ${i.slug}`.toLowerCase().includes(term);
    });
  }, [items, filter, q]);

  const workflow = CONTENT_KINDS[kind].workflow;

  async function run(action: "publish" | "unpublish", force = false) {
    if (selected.size === 0) return;
    setBusy(true); setNote(null);
    try {
      const res = await publish({ data: { kind, ids: [...selected], action, force } });
      const failed = res.results.filter((r) => !r.ok);
      setNote(
        failed.length === 0
          ? `${res.results.length} record(s) ${action === "publish" ? "published" : "unpublished"}. Sitemap and structured data regenerate on next request.`
          : `${res.results.length - failed.length} succeeded, ${failed.length} blocked: ${failed.map((f) => f.error).join("; ")}`,
      );
      await loadList(kind);
      await loadOverview();
    } catch (e) {
      setNote((e as Error).message);
    }
    setBusy(false);
  }

  async function runSchedule(action: "publish" | "unpublish") {
    if (selected.size === 0 || !when) return;
    setBusy(true); setNote(null);
    try {
      const res = await schedule({ data: { kind, ids: [...selected], action, scheduledAt: new Date(when).toISOString() } });
      setNote(res.ok ? `Scheduled ${selected.size} record(s) for ${new Date(when).toLocaleString()}.` : res.errors.join("; "));
      await loadList(kind);
      await loadOverview();
    } catch (e) {
      setNote((e as Error).message);
    }
    setBusy(false);
  }

  return (
    <AdminSection
      title="Content operations"
      description="One draft → scheduled → published workflow across every content surface, with quality checks, version snapshots and publishing analytics."
    >
      {/* Overview */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {overview && (
          <>
            <Stat label="Records tracked" value={String(overview.total)} />
            <Stat label="Publishes (30d)" value={String(overview.analytics.publishes)} />
            <Stat label="Blocked by checks" value={String(overview.analytics.blocked)} />
            <Stat
              label="Content revision"
              value={String(overview.analytics.state.revision)}
              hint={overview.analytics.state.last_publish_at ? `last ${new Date(overview.analytics.state.last_publish_at).toLocaleString()}` : "no publishes yet"}
            />
          </>
        )}
      </div>

      {/* Kind picker */}
      <nav aria-label="Content kinds" className="mt-8 flex flex-wrap gap-2">
        {CONTENT_KIND_IDS.map((k) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            aria-pressed={k === kind}
            className={`rounded-full px-3 py-1.5 text-xs uppercase tracking-[0.2em] transition ${
              k === kind ? "bg-gold-gradient text-obsidian font-semibold" : "border hairline text-muted-foreground hover:text-foreground"
            }`}
          >
            {CONTENT_KINDS[k].label}
          </button>
        ))}
      </nav>

      {/* Controls */}
      <div className="mt-5 flex flex-wrap items-end gap-3">
        <div className="w-56">
          <Field label="Search"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="title or slug" /></Field>
        </div>
        <div>
          <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">State</span>
          <div className="mt-2 flex gap-2">
            {(["all", "draft", "scheduled", "published"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                aria-pressed={filter === f}
                className={`rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] ${
                  filter === f ? "border-gold text-gold" : "hairline text-muted-foreground"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="w-56">
          <Field label="Schedule for"><Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} /></Field>
        </div>
      </div>

      {workflow !== "always" && (
        <div className="mt-4 flex flex-wrap gap-2">
          <AdminButton disabled={busy || selected.size === 0} onClick={() => run("publish")}>
            Publish {selected.size || ""}
          </AdminButton>
          <AdminButton variant="ghost" disabled={busy || selected.size === 0} onClick={() => run("unpublish")}>
            Unpublish
          </AdminButton>
          <AdminButton variant="ghost" disabled={busy || selected.size === 0 || !when} onClick={() => runSchedule("publish")}>
            Schedule publish
          </AdminButton>
          <AdminButton variant="ghost" disabled={busy || selected.size === 0 || !when} onClick={() => runSchedule("unpublish")}>
            Schedule unpublish
          </AdminButton>
          <AdminButton variant="danger" disabled={busy || selected.size === 0} onClick={() => run("publish", true)}>
            Force publish
          </AdminButton>
        </div>
      )}
      {workflow === "always" && (
        <p className="mt-4 text-xs text-muted-foreground">
          {CONTENT_KINDS[kind].label} records have no publish state in the database — this view runs quality checks,
          previews and version snapshots only.
        </p>
      )}

      {note && <p className="mt-4 rounded-md border hairline px-3 py-2 text-sm text-muted-foreground">{note}</p>}
      {error && <p className="mt-4 text-sm text-red-400">{error}</p>}

      <div className="mt-6">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-muted-foreground">No records match.</p>
        ) : (
          <TableShell
            headers={["", "Title", "State", "Checks", "Scheduled", ""]}
            rows={visible.map((i) => (
              <tr key={i.id} className="hover:bg-obsidian/40">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    aria-label={`Select ${i.title}`}
                    checked={selected.has(i.id)}
                    onChange={(e) => {
                      const next = new Set(selected);
                      e.target.checked ? next.add(i.id) : next.delete(i.id);
                      setSelected(next);
                    }}
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="font-serif text-base text-gold-luminous">{i.title || "(untitled)"}</div>
                  <div className="text-xs text-muted-foreground">{i.path ?? `/${i.slug}`}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] ${STATE_STYLE[i.state]}`}>
                    {i.state}
                  </span>
                </td>
                <td className="px-4 py-3"><IssueDots issues={i.issues} /></td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {i.scheduledFor ? (
                    <span>
                      {i.scheduleAction} · {new Date(i.scheduledFor).toLocaleString()}{" "}
                      <button
                        className="underline"
                        onClick={async () => { await cancel({ data: { scheduleId: i.scheduleId! } }); void loadList(kind); void loadOverview(); }}
                      >
                        cancel
                      </button>
                    </span>
                  ) : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Link
                      to="/admin/preview/$kind/$id"
                      params={{ kind, id: i.id }}
                      className="rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-gold"
                    >
                      Preview
                    </Link>
                    <AdminButton variant="ghost" onClick={() => setInspect(i)}>Details</AdminButton>
                    <AdminButton
                      variant="ghost"
                      onClick={async () => { await snapshot({ data: { kind, id: i.id, label: "manual" } }); setNote("Version snapshot saved."); }}
                    >
                      Snapshot
                    </AdminButton>
                  </div>
                </td>
              </tr>
            ))}
          />
        )}
      </div>

      {/* Upcoming schedule + recent activity */}
      {overview && (
        <div className="mt-12 grid gap-8 lg:grid-cols-2">
          <div>
            <h3 className="font-serif text-xl text-gold-luminous">Upcoming schedule</h3>
            <ul className="mt-3 space-y-2 text-sm">
              {overview.schedules.filter((s: any) => s.state === "pending").slice(0, 10).map((s: any) => (
                <li key={s.id} className="flex justify-between gap-3 border-b hairline pb-2">
                  <span>{s.title || s.slug} <span className="text-xs text-muted-foreground">({s.kind})</span></span>
                  <span className="text-xs text-muted-foreground">{s.action} · {new Date(s.scheduled_at).toLocaleString()}</span>
                </li>
              ))}
              {overview.schedules.filter((s: any) => s.state === "pending").length === 0 && (
                <li className="text-sm text-muted-foreground">Nothing scheduled.</li>
              )}
            </ul>
          </div>
          <div>
            <h3 className="font-serif text-xl text-gold-luminous">Recent publishing activity</h3>
            <ul className="mt-3 space-y-2 text-sm">
              {overview.analytics.recent.slice(0, 10).map((e: any, idx: number) => (
                <li key={idx} className="flex justify-between gap-3 border-b hairline pb-2">
                  <span className={e.ok ? "" : "text-red-400"}>{e.action} · {e.kind}{e.slug ? ` · ${e.slug}` : ""}</span>
                  <span className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</span>
                </li>
              ))}
              {overview.analytics.recent.length === 0 && <li className="text-sm text-muted-foreground">No activity yet.</li>}
            </ul>
          </div>
        </div>
      )}

      {inspect && <DetailsDialog item={inspect} kind={kind} onClose={() => setInspect(null)} onChanged={() => { void loadList(kind); }} />}
    </AdminSection>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border hairline p-4">
      <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{label}</p>
      <p className="mt-2 font-serif text-3xl text-gold-luminous">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground/70">{hint}</p>}
    </div>
  );
}

function DetailsDialog({ item, kind, onClose, onChanged }: {
  item: Item; kind: ContentKindId; onClose: () => void; onChanged: () => void;
}) {
  const versionsFn = useServerFn(contentVersions);
  const relatedFn = useServerFn(relatedContent);
  const restoreFn = useServerFn(restoreContentVersion);
  const [versions, setVersions] = useState<any[]>([]);
  const [related, setRelated] = useState<any[]>([]);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    void versionsFn({ data: { kind, id: item.id } }).then(setVersions).catch(() => setVersions([]));
    void relatedFn({ data: { kind, id: item.id } }).then((r: any) => setRelated(r.suggestions)).catch(() => setRelated([]));
  }, [item.id, kind]);

  return (
    <AdminDialog open onClose={onClose} title={item.title || item.slug}>
      <div className="space-y-6 text-sm">
        <section>
          <h4 className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Quality checks</h4>
          <ul className="mt-2 space-y-1">
            {item.issues.length === 0 && <li className="text-emerald">All checks pass.</li>}
            {item.issues.map((i) => (
              <li key={i.code} className={i.level === "error" ? "text-red-400" : i.level === "warn" ? "text-amber-300" : "text-muted-foreground"}>
                [{i.level}] {i.message}
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h4 className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Suggested related content</h4>
          <ul className="mt-2 space-y-1">
            {related.length === 0 && <li className="text-muted-foreground">No strong matches.</li>}
            {related.map((r) => (
              <li key={`${r.kind}-${r.id}`} className="flex justify-between gap-3">
                <span>{r.title} <span className="text-xs text-muted-foreground">({r.kind})</span></span>
                <span className="text-xs text-muted-foreground">score {r.score} · {r.shared.slice(0, 3).join(", ")}</span>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h4 className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Version history</h4>
          <ul className="mt-2 space-y-1">
            {versions.length === 0 && <li className="text-muted-foreground">No snapshots yet.</li>}
            {versions.map((v) => (
              <li key={v.id} className="flex items-center justify-between gap-3">
                <span>{v.label ?? "snapshot"} <span className="text-xs text-muted-foreground">{new Date(v.created_at).toLocaleString()}</span></span>
                <AdminButton
                  variant="ghost"
                  onClick={async () => {
                    if (!confirm("Restore this version? The current state is snapshotted first.")) return;
                    await restoreFn({ data: { versionId: v.id } });
                    setMsg("Restored.");
                    onChanged();
                  }}
                >
                  Restore
                </AdminButton>
              </li>
            ))}
          </ul>
        </section>

        {msg && <p className="text-emerald">{msg}</p>}
      </div>
    </AdminDialog>
  );
}
